import React, { useState, useEffect, useCallback } from 'react';
import ChatWindow from './components/ChatWindow';
import Sidebar from './components/Sidebar';
import SharedChatView from './components/SharedChatView';
import { chatStorage, Chat, Message } from './utils/chatStorage';
import { track } from './utils/analytics';
import mgLogo from './assets/mygate_logo.jpg';
import './App.css';

function getShareId(): string | null {
  const match = window.location.pathname.match(/^\/share\/([A-Za-z0-9]+)$/);
  return match ? match[1] : null;
}

// ===========================
// MAIN CHAT APP
// ===========================

function ChatApp() {
  const [chats, setChats] = useState<Chat[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 769);
  const [escalatedChats, setEscalatedChats] = useState<Record<string, { ticketId: string; email: string }>>({});
  const [raiseTicketChatId, setRaiseTicketChatId] = useState<string | null>(null);

  useEffect(() => {
    const loadedChats = chatStorage.getAllChats();
    setChats(loadedChats);

    if (loadedChats.length === 0) {
      const newChat = chatStorage.createChat();
      setChats([newChat]);
      setCurrentChatId(newChat.id);
    } else {
      setCurrentChatId(loadedChats[0].id);
    }
  }, []);

  const handleNewChat = (initialModule?: string) => {
    track('new_chat');
    const newChat = chatStorage.createChat();
    if (initialModule !== undefined) {
      chatStorage.updateChat(newChat.id, { selectedModule: initialModule });
      newChat.selectedModule = initialModule;
    }
    setChats([newChat, ...chats]);
    setCurrentChatId(newChat.id);
  };

  const handleSelectChat = (chatId: string) => {
    setCurrentChatId(chatId);
    if (window.innerWidth < 769) setSidebarOpen(false);
  };

  const handleDeleteChat = (chatId: string) => {
    chatStorage.deleteChat(chatId);
    const updatedChats = chats.filter(chat => chat.id !== chatId);
    setChats(updatedChats);

    if (chatId === currentChatId) {
      if (updatedChats.length > 0) {
        setCurrentChatId(updatedChats[0].id);
      } else {
        const newChat = chatStorage.createChat();
        setChats([newChat]);
        setCurrentChatId(newChat.id);
      }
    }
  };

  const handleUpdateMessages = useCallback((chatId: string, messages: Message[], title?: string) => {
    const updates: Partial<Chat> = { messages };
    if (title) updates.title = title;
    chatStorage.updateChat(chatId, updates);
    setChats(prevChats =>
      prevChats.map(chat =>
        chat.id === chatId ? { ...chat, ...updates, updatedAt: Date.now() } : chat
      )
    );
  }, []);

  const handleModuleChange = useCallback((chatId: string, module: string | undefined) => {
    chatStorage.updateChat(chatId, { selectedModule: module });
    setChats(prevChats =>
      prevChats.map(chat =>
        chat.id === chatId ? { ...chat, selectedModule: module, updatedAt: Date.now() } : chat
      )
    );
  }, []);

  const handleRaiseTicketEnabled = useCallback((chatId: string) => {
    chatStorage.updateChat(chatId, { raiseTicket: true });
    setChats(prev => prev.map(c => c.id === chatId ? { ...c, raiseTicket: true } : c));
  }, []);

  const handleNewChatWithModule = useCallback((module: string | undefined) => {
    handleNewChat(module);
  }, [chats]);

  const currentChat = chats.find(chat => chat.id === currentChatId);

  return (
    <div className={`App ${sidebarOpen ? 'sidebar-is-open' : ''}`}>
      <Sidebar
        chats={chats}
        currentChatId={currentChatId}
        onSelectChat={handleSelectChat}
        onNewChat={handleNewChat}
        onDeleteChat={handleDeleteChat}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onTicketRaised={(chatId, ticketId, email) =>
          setEscalatedChats(prev => ({ ...prev, [chatId]: { ticketId, email } }))
        }
        raiseTicketChatId={raiseTicketChatId}
        onClearRaiseTicketChatId={() => setRaiseTicketChatId(null)}
      />

      <div className="app-main">
        <header className="App-header">
          <div className="header-left">
            <button className="header-sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} title="Toggle sidebar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div className="header-brand">
              <div className="header-logo-mark">
                <img src={mgLogo} alt="Mygate" />
              </div>
              <div className="header-divider" />
              <div className="header-brand-text">
                <h1>MIRA <span>One</span></h1>
                <p>Mygate Intelligent Response Assistant</p>
              </div>
            </div>
          </div>
        </header>
        <main>
          {currentChat && (
            <ChatWindow
              key={currentChat.id}
              chatId={currentChat.id}
              initialMessages={currentChat.messages}
              onUpdateMessages={handleUpdateMessages}
              selectedModule={currentChat.selectedModule}
              onModuleChange={(module) => handleModuleChange(currentChat.id, module)}
              onNewChat={handleNewChatWithModule}
              chatTitle={currentChat.title}
              escalation={escalatedChats[currentChat.id]}
              raiseTicketEnabled={currentChat.raiseTicket}
              onRaiseTicketEnabled={handleRaiseTicketEnabled}
              onRaiseTicketClick={() => setRaiseTicketChatId(currentChat.id)}
            />
          )}
        </main>
      </div>
    </div>
  );
}

// ===========================
// ROOT — share route detection
// ===========================

function App() {
  const shareId = getShareId();

  const handleContinueSharedChat = useCallback((messages: Message[], module: string | undefined) => {
    const newChat = chatStorage.createChat();
    chatStorage.updateChat(newChat.id, { messages, selectedModule: module });
    window.history.pushState({}, '', '/');
    window.location.reload();
  }, []);

  if (shareId) {
    return <ShareApp shareId={shareId} onContinue={handleContinueSharedChat} />;
  }

  return <ChatApp />;
}

function ShareApp({ shareId, onContinue }: { shareId: string; onContinue: (messages: Message[], module: string | undefined) => void }) {
  const [chats, setChats] = useState<Chat[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 769);

  useEffect(() => {
    const loadedChats = chatStorage.getAllChats();
    setChats(loadedChats);
    if (loadedChats.length > 0) setCurrentChatId(loadedChats[0].id);
  }, []);

  const handleSelectChat = (chatId: string) => {
    window.history.pushState({}, '', '/');
    window.location.reload();
  };

  return (
    <div className={`App ${sidebarOpen ? 'sidebar-is-open' : ''}`}>
      <Sidebar
        chats={chats}
        currentChatId={currentChatId}
        onSelectChat={handleSelectChat}
        onNewChat={() => { window.history.pushState({}, '', '/'); window.location.reload(); }}
        onDeleteChat={(chatId) => {
          chatStorage.deleteChat(chatId);
          setChats(prev => prev.filter(c => c.id !== chatId));
        }}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(o => !o)}
      />
      <div className="app-main">
        <header className="App-header">
          <div className="header-left">
            <button className="header-sidebar-toggle" onClick={() => setSidebarOpen(o => !o)} title="Toggle sidebar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div className="header-brand">
              <div className="header-logo-mark">
                <img src={mgLogo} alt="Mygate" />
              </div>
              <div className="header-divider" />
              <div className="header-brand-text">
                <h1>MIRA <span>One</span></h1>
                <p>Mygate Intelligent Response Assistant</p>
              </div>
            </div>
          </div>
        </header>
        <main>
          <SharedChatView shareId={shareId} onContinueInApp={onContinue} />
        </main>
      </div>
    </div>
  );
}

export default App;
